import React, { useContext } from 'react';
import { bool, oneOf, string } from 'prop-types';
import classNames from 'classnames/bind';
import { Link, Typography } from '@one-thd/sui-atomic-components';
import { StorePodContext } from '../StorePod.context';
import { StorePodLoader } from './StorePodLoader';

const cx = classNames.bind();

export const StorePodPhoneNumber = ({
  boldLabel,
  displayMultiline,
  hideLabel,
  type,
  showIcon,
  height
}) => {
  const { isDesignStore, loading, store } = useContext(StorePodContext);
  const { toolRental: hasToolRentalService } = store?.services || {};

  const isPro = type === 'pro';
  const isTool = type === 'tool';

  const unavailableToolRental = isTool && !hasToolRentalService;
  const invalidDesignStoreService = isDesignStore && (isTool || isPro);
  const shouldHidePhone = unavailableToolRental || invalidDesignStoreService;

  const phoneMap = {
    pro: {
      icon: 'pro-icon.svg',
      label: 'Pro Service Desk',
      number: store?.proDeskPhone
    },
    services: {
      icon: null,
      label: 'Home Services',
      number: store?.storeServicesPhone
    },
    store: {
      icon: null,
      label: isDesignStore ? 'Design Center' : 'Store',
      number: store?.phone
    },
    tool: {
      icon: 'rental-icon.svg',
      label: 'Tool & Truck Rental',
      number: store?.toolRentalPhone
    }
  };

  const { icon, label, number } = phoneMap[type];

  const iconClasses = cx('sui-pr-2 sui-object-contain', {
    'self-baseline sui-pr-2': showIcon && displayMultiline
  });

  const phoneNumberClasses = cx('sui-flex', {
    'sui-flex sui-flex-col': displayMultiline && !showIcon,
    'sui-items-center': !displayMultiline
  });

  const wrapperClasses = cx('sui-overflow-hidden sui-text-ellipsis', {
    'sui-flex': !displayMultiline
  });

  if (shouldHidePhone) return null;

  if (loading) {
    return (
      <div
        data-testid="store-pod-phone-number-loader"
        className={phoneNumberClasses}
        data-component="StorePodPhoneNumberLoader"
      >
        <div style={{ width: '9.375rem' }}>
          <StorePodLoader height="2rem" />
        </div>
        {displayMultiline && (
          <div className="sui-m-1" style={{ width: '10.938rem' }}>
            <StorePodLoader height="1rem" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={phoneNumberClasses} data-testid="store-pod-phone-number" data-component="StorePodPhoneNumber">
      {showIcon && (
        <img
          alt={label}
          className={iconClasses}
          height="50"
          width="50"
          src={`https://assets.homedepot-static.com/images/v1/${icon}`}
        />
      )}
      <div className={wrapperClasses}>
        <Typography
          height={height}
          weight={boldLabel ? 'bold' : 'regular'}
          variant="body-base"
        >
          {!hideLabel && (
            <>
              {label}:&nbsp;
            </>
          )}
          {displayMultiline && (<br />)}
        </Typography>
        <Typography
          height={height}
          weight="regular"
          variant="body-base"
        >
          <Link href={'tel:' + number} target="_parent">
            {number}
          </Link>
        </Typography>
      </div>
    </div>
  );
};

StorePodPhoneNumber.propTypes = {
  boldLabel: bool,
  displayMultiline: bool,
  hideLabel: bool,
  type: oneOf(['tool', 'store', 'pro', 'services']),
  showIcon: bool,
  height: string
};

StorePodPhoneNumber.defaultProps = {
  boldLabel: false,
  displayMultiline: false,
  hideLabel: false,
  type: 'store',
  showIcon: false,
  height: 'tight'
};

StorePodPhoneNumber.displayName = 'StorePodPhoneNumber';
